import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { DashboardUtils } from 'src/app/dashboards/DashboardUtils';
import {
  DashboardConfigurationDependencies,
  Granularity,
  Metric,
} from 'src/app/dashboards/shared/dashboard-config/dashboard-config.model';
import { DashboardDataController } from 'src/app/dashboards/shared/data_controller/dashboard_data_controller';
import { ProductMethodologiesLimiterService } from 'src/app/dashboards/shared/services/product-methodologies-limiter.service';
import { DatasetsService } from 'src/app/shared/services/datasets.service';
import { LocalDatasetsService } from 'src/app/shared/services/local-datasets.service';
import { DataSlice } from 'src/app/state/library/library.model';
import { LocalDatasetsState } from 'src/app/state/local_datasets/local_datasets.state';
import { ProductsState } from 'src/app/state/products/products.state';
import { SliceDashboardConfig } from '../dashboard-configs/slice-dashboard-config.model';
import { getSliceDefinition, buildSliceDashboardConfig } from './slice-definition-shell';
import { Location } from '@angular/common';

@Injectable({ providedIn: 'root' })
export class SliceDashboardConfigFactory {

  constructor(
    private datasetsService: DatasetsService,
    private localDatasetsService: LocalDatasetsService,
    private store: Store,
    private router: Router,
    private methodologiesLimiterService: ProductMethodologiesLimiterService,
    private location: Location,
  ) {}

  /**
   * Resolve dashboard wiring for a saved slice via `slice.shellId` → definition getters.
   * Returns null when shellId/definition is missing (legacy or unsupported).
   */
  resolve(slice: DataSlice, controller: DashboardDataController): SliceDashboardConfig | null {
    try {
      const definition = getSliceDefinition(slice);
      if (!definition) {
        return null;
      }

      const decoded = DashboardUtils.decodeDashboardId(slice.dashboardId);
      const deps = this._buildDeps(slice, decoded, controller);
      return buildSliceDashboardConfig(definition, deps);
    } catch (error) {
      console.warn('[SliceDashboardConfigFactory] Failed to resolve config for', slice.dashboardId, error);
      return null;
    }
  }

  private _buildDeps(
    slice: DataSlice,
    decoded: ReturnType<typeof DashboardUtils.decodeDashboardId>,
    controller: DashboardDataController,
  ): DashboardConfigurationDependencies {
    return {
      datasetsService: this.datasetsService,
      localDatasetsService: this.localDatasetsService,
      dashboardDataController: controller,
      activatedRoute: this._stubActivatedRoute(slice.dashboardId, slice.dashboardVariant),
      store: this.store,
      router: this.router,
      methodologiesLimiterService: this.methodologiesLimiterService,
      sumSubMarketIds: this.store.selectSnapshot(LocalDatasetsState.sumSubMarketIds).map((id) => id.toString()),
      datasetName: slice.datasetName,
      granularityId: decoded.granularityId as Granularity,
      metricId: decoded.metricId as Metric,
      location: this.location
    };
  }

  /**
   * Minimal ActivatedRoute for headless filter construction.
   * Includes product methodology route data so dossier overview filters
   * (sub_market default + methodology onlyShow) can initialize.
   */
  private _stubActivatedRoute(dashboardId: string, variant?: string): ActivatedRoute {
    const url = [{ path: dashboardId }];
    if (variant) {
      url.push({ path: variant });
    }

    const selectedProduct = this.store.selectSnapshot(ProductsState.selectedProduct);
    const defaultMethodology = selectedProduct?.defaultMethodologyId?.toString() ?? '1';
    const methodologies =
      selectedProduct?.productMethodologies
        ?.map((pm) => pm?.methodologyId?.toString())
        .filter((id): id is string => !!id) ?? [defaultMethodology];

    return {
      snapshot: {
        url,
        data: {
          defaultMethodology,
          methodologies,
        },
      },
    } as unknown as ActivatedRoute;
  }
}
